import type { TreeData } from '../../components/tree/useTreeApi';
import type { AllSelectionListsWithSkillTaxonomyKey } from '../types';
import type { TaxonomyLevelMetadata } from './taxonomyTree';

export type CompoundIdItem = {
  id: string;
  shortName: string;
  longName: string;
  nodeCount?: number;
};

export type CompoundIdSelectionLists = Partial<
  Record<AllSelectionListsWithSkillTaxonomyKey, CompoundIdItem[]>
>;

export const flattenTreeToCompoundIdItems = (
  treeData: readonly TreeData[],
  levels: readonly TaxonomyLevelMetadata[]
): CompoundIdSelectionLists => {
  // Sort ascending by id so depth 0 → first level tag, depth 1 → second, etc.
  const levelKeyByDepth = [...levels]
    .sort((a, b) => a.id - b.id)
    .map(
      (level) =>
        (level.tag ?? null) as AllSelectionListsWithSkillTaxonomyKey | null
    );

  const result: CompoundIdSelectionLists = {};
  const seenIds = new Map<AllSelectionListsWithSkillTaxonomyKey, Set<string>>();

  const visit = (
    nodes: readonly TreeData[],
    ancestorRawIds: readonly string[],
    recursionDepth = 0
  ): void => {
    for (const node of nodes) {
      const depth = node.depth ?? recursionDepth;
      const rawId = String(node.id);
      const compoundId = [...ancestorRawIds, rawId].join('_');
      const key = levelKeyByDepth[depth];

      if (key != null) {
        let ids = seenIds.get(key);
        if (!ids) {
          ids = new Set();
          seenIds.set(key, ids);
          result[key] = [];
        }
        if (!ids.has(compoundId)) {
          ids.add(compoundId);
          result[key]?.push({
            id: compoundId,
            shortName: node.name,
            longName: node.name,
            nodeCount: node.nodeCount,
          });
        }
      }

      if (node.children) {
        visit(node.children, [...ancestorRawIds, rawId], depth + 1);
      }
    }
  };

  visit(treeData, []);
  return result;
};
